import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import ActivitiesStepShell from '@/components/step-shells/ActivitiesStepShell';
import { useCourse } from '@/contexts/CourseContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const parseDuration = (value) => {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  const cleaned = `${value}`.replace(/[^\d.]/g, '');
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeGoalEntries = (rawGoals) => {
  const goalsArray = Array.isArray(rawGoals)
    ? rawGoals
    : rawGoals
      ? [rawGoals]
      : [];

  return goalsArray
    .map((goal) => {
      if (typeof goal === 'string') return goal;
      if (typeof goal === 'object') {
        return goal.text || goal.value || goal.title || goal.description || '';
      }
      return '';
    })
    .map((goal) => goal && goal.trim())
    .filter(Boolean);
};

/**
 * ImportStep3Activities
 * Activities planning phase where the ImportConversationAgent:
 * 1. Reviews/plans activities structure
 * 2. Chats with user to refine activities and topics
 * 3. Helps organize course content
 * 4. Finalizes the improved course
 */
export default function ImportStep3Activities({ courseId, importSessionId, courseData, onComplete, setCustomChatHandler, updateCourseData }) {
  const { dispatch } = useCourse();
  const [topics, setTopics] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const ensureConversationReady = useCallback(async () => {
    if (!importSessionId) return false;
    const maxAttempts = 40;
    const delayMs = 600;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const statusResp = await axios.get(`${API_URL}/import/session-status/${importSessionId}`);
        const ready = statusResp.data.ready_for_conversation || statusResp.data.next_step === 'conversation';
        const goalsLocked = statusResp.data.goals_locked;
        if (ready && goalsLocked) {
          return true;
        }
      } catch (error) {
        console.warn('Failed to fetch session status for conversation readiness:', error);
        // Keep retrying even if the request occasionally fails
      }

      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }

    return false;
  }, [importSessionId]);

  // CRITICAL: Set import mode when component mounts
  useEffect(() => {
    if (importSessionId) {
      dispatch({
        type: 'SET_IMPORT_MODE',
        payload: { sessionId: importSessionId, executionPlan: null }
      });
    }
  }, [importSessionId, dispatch]);

  // Initialize topics from courseData AND send welcome message (like Step 2 does)
  useEffect(() => {
    if (!courseData?.topics || initialized) return;

    // Mark as initialized FIRST
    setInitialized(true);

    // Set topics
    setTopics(courseData.topics);

    // Clear chat from previous step
    dispatch({ type: 'SET_CHAT_HISTORY', payload: [] });

    // Show loading indicator
    dispatch({
      type: 'ADD_CHAT_MESSAGE', payload: {
        role: 'assistant',
        isLoading: true
      }
    });

    const topicCount = courseData.topics.length;
    const activityCount = courseData.topics.reduce((sum, t) =>
      sum + (t.activities?.length || t.Activities?.length || 0), 0);

    // Get dynamic welcome from agent (includes warnings if any issues)
    // Get dynamic welcome from agent (includes warnings if any issues)
    const getAgentWelcome = async () => {
      // Calculate orphan goals (goals not linked to any activity)
      const allGoals = courseData?.learningObjectives || courseData?.goals || [];
      const linkedGoalIds = new Set();

      courseData.topics.forEach(t => {
        (t.activities || t.Activities || []).forEach(a => {
          // Check both legacy and new ID fields and array/string variants
          const ids = a.objectiveIds || a.goalIds || [];
          const id = a.objectiveId || a.goalId;

          ids.forEach(gid => linkedGoalIds.add(gid));
          if (id) linkedGoalIds.add(id);
        });
      });

      const orphanGoals = allGoals.filter(g => !linkedGoalIds.has(g.id));
      const orphanCount = orphanGoals.length;
      const orphanContext = orphanCount > 0
        ? `\n\n**CRITICAL OBSERVATION**: I detected ${orphanCount} "orphan" learning objective(s) that are NOT linked to any activities properly. You MUST mention this to the user immediately as the first thing to fix.`
        : '';

      try {
        const id = courseId || importSessionId;
        const response = await axios.post(`${API_URL}/chat/step3`, {
          courseId: id,
          message: `(System Instruction) The user has just entered Step 3 (Activities).
I found ${topicCount} topics with ${activityCount} activities.${orphanContext}

Please introduce this step to the user. Explain that we will now review and refine the activities.
If there are orphan learning objectives (mentioned above), please prioritize pointing them out.
Otherwise, use your own natural style to welcome the user and ask how they'd like to proceed.`
        });

        dispatch({ type: 'REMOVE_LAST_MESSAGE' });
        dispatch({
          type: 'ADD_CHAT_MESSAGE', payload: {
            role: 'assistant',
            text: response.data.response || `Καλωσήρθατε στο Βήμα 3! Τώρα θα δούμε τις δραστηριότητες του μαθήματός σας. Ας δούμε τι έχουμε!\n\nΒρήκα ${topicCount} θέματα με ${activityCount} δραστηριότητες. Μπορώ να σας βοηθήσω να επεξεργαστείτε δραστηριότητες, να οργανώσετε θέματα, και να βελτιώσετε τους πόρους. Από πού θέλετε να ξεκινήσουμε;`
          }
        });
      } catch (error) {
        console.error('Error getting agent welcome:', error);
        dispatch({ type: 'REMOVE_LAST_MESSAGE' });
        dispatch({
          type: 'ADD_CHAT_MESSAGE', payload: {
            role: 'assistant',
            text: `Καλωσήρθατε στο Βήμα 3! Τώρα θα δούμε τις δραστηριότητες του μαθήματός σας. Ας δούμε τι έχουμε!\n\nΒρήκα ${topicCount} θέματα με ${activityCount} δραστηριότητες. Από πού θέλετε να ξεκινήσουμε;`
          }
        });
      }
    };
    getAgentWelcome();
  }, [courseData, initialized, courseId, importSessionId, dispatch]);

  // Custom send handler for activities/conversation
  const handleSendMessage = useCallback(async (userMessage) => {
    if (!userMessage.trim() || isProcessing) return;

    setIsProcessing(true);
    dispatch({ type: 'SET_LOADING', payload: true });

    // Remove any existing loading indicators first to prevent duplicates
    dispatch({ type: 'REMOVE_LOADING_MESSAGES' });

    // Add user message to chat
    dispatch({ type: 'ADD_CHAT_MESSAGE', payload: { role: 'user', text: userMessage } });

    // Add typing indicator
    dispatch({ type: 'ADD_CHAT_MESSAGE', payload: { role: 'assistant', isLoading: true } });

    try {
      // Use unified chat endpoint for Step 3
      const id = courseId || importSessionId;
      const response = await axios.post(`${API_URL}/chat/step3`, {
        courseId: id,
        message: userMessage
      });

      // Remove typing indicator
      dispatch({ type: 'REMOVE_LAST_MESSAGE' });

      // Add assistant response
      const assistantText = response.data.response || response.data.message || ' ';
      dispatch({
        type: 'ADD_CHAT_MESSAGE', payload: {
          role: 'assistant',
          text: assistantText
        }
      });

      // CRITICAL: Check for updated learning objectives (e.g. auto-generated ones)
      // The backend may generate new objectives when creating activities
      const updatedObjectives =
        response.data.course_data?.learningObjectives ||
        response.data.course_data?.goals;

      if (updatedObjectives && Array.isArray(updatedObjectives) && updateCourseData) {
        // We must update the global course state so that normalizedTopics can resolve the new IDs
        // The useMemo hook depends on courseData, so this will trigger a re-render
        console.log(`Backend returned ${updatedObjectives.length} objectives - updating global course data`);

        // We need to preserve topics as they are handled locally until explicitly updated
        // But we DO want to update objectives immediately
        const updatePayload = {};
        if (response.data.course_data?.learningObjectives) {
          updatePayload.learningObjectives = response.data.course_data.learningObjectives;
        }
        if (response.data.course_data?.goals) {
          updatePayload.goals = response.data.course_data.goals;
        }

        updateCourseData(updatePayload);
      }

      // Update topics if provided
      // CRITICAL: Accept empty arrays from backend as valid updates (topics were deleted)
      if (response.data.course_data && response.data.course_data.topics && Array.isArray(response.data.course_data.topics)) {
        const backendTopics = response.data.course_data.topics;

        if (backendTopics.length > 0) {
          // Backend has topics - merge objectiveIds/objectiveId from current state to preserve objective links
          // Support both new (objectiveId/objectiveIds) and legacy (goalId/goalIds) field names
          setTopics((currentTopics) => {
            // Build a map of current activities by ID to preserve objective links
            const currentActivityMap = new Map();
            currentTopics.forEach((topic) => {
              if (topic.activities) {
                topic.activities.forEach((activity) => {
                  if (activity.id) {
                    currentActivityMap.set(activity.id, activity);
                  }
                });
              }
            });

            // Merge objectiveIds/objectiveId from current state into backend topics
            const mergedTopics = backendTopics.map((backendTopic) => {
              const backendActivities = backendTopic.activities || backendTopic.Activities || [];
              const mergedActivities = backendActivities.map((backendActivity) => {
                const activityId = backendActivity.id || backendActivity.activityId || backendActivity.Id;
                const currentActivity = activityId ? currentActivityMap.get(activityId) : null;

                // Check if backend has objective links (support both new and legacy field names)
                const hasBackendObjectiveIds = backendActivity.objectiveIds || backendActivity.goalIds;
                const hasBackendObjectiveId = backendActivity.objectiveId || backendActivity.goalId;

                // Preserve objective links from current state if backend doesn't have them
                if (currentActivity) {
                  if (!hasBackendObjectiveIds && !hasBackendObjectiveId) {
                    // Backend doesn't have objective links, preserve from current state
                    const currentObjectiveIds = currentActivity.objectiveIds || currentActivity.goalIds;
                    const currentObjectiveId = currentActivity.objectiveId || currentActivity.goalId;
                    if (currentObjectiveIds && currentObjectiveIds.length > 0) {
                      backendActivity.objectiveIds = [...currentObjectiveIds];
                    } else if (currentObjectiveId) {
                      backendActivity.objectiveId = currentObjectiveId;
                      backendActivity.objectiveIds = [currentObjectiveId];
                    }
                  } else if (hasBackendObjectiveId && !hasBackendObjectiveIds) {
                    // Backend has objectiveId but not objectiveIds, ensure objectiveIds exists
                    backendActivity.objectiveIds = [backendActivity.objectiveId || backendActivity.goalId];
                  }
                }

                return backendActivity;
              });

              return {
                ...backendTopic,
                activities: mergedActivities
              };
            });

            console.log(`Backend returned ${backendTopics.length} topics - updating state with preserved objective links`);
            return mergedTopics;
          });
        } else {
          // Backend returned empty topics array - this means topics were deleted, so update UI to empty
          console.log(`✅ Backend returned empty topics array - updating UI to reflect deletion (all topics deleted)`);
          setTopics([]);
        }
      } else if (response.data.course_data && !response.data.course_data.topics) {
        // Backend didn't include topics at all - preserve existing ones (not a deletion operation)
        console.log('Backend response did not include topics - preserving existing topics');
      }

      const sessionCompleted = Boolean(response.data.completed);

      if (sessionCompleted && onComplete) {
        onComplete(response.data);
        return;
      }

    } catch (error) {
      console.error('Error sending message:', error);
      dispatch({ type: 'REMOVE_LAST_MESSAGE' }); // Remove typing indicator
      dispatch({
        type: 'ADD_CHAT_MESSAGE', payload: {
          role: 'assistant',
          text: '❌ Συγγνώμη, παρουσιάστηκε σφάλμα. Παρακαλώ δοκιμάστε ξανά.'
        }
      });
    } finally {
      setIsProcessing(false);
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [importSessionId, isProcessing, onComplete, dispatch]);

  // Register custom chat handler with parent using Ref pattern to avoid infinite loops
  const handlerRef = React.useRef(handleSendMessage);
  useEffect(() => {
    handlerRef.current = handleSendMessage;
  });

  useEffect(() => {
    if (setCustomChatHandler) {
      setCustomChatHandler(() => (msg) => handlerRef.current(msg));
    }
    return () => {
      if (setCustomChatHandler) {
        setCustomChatHandler(null);
      }
    };
  }, [setCustomChatHandler]);

  // Normalize topics to have consistent shape with id
  const normalizedTopics = useMemo(
    () => {
      // SUPPORT BOTH SCHEMA FORMATS: 'goals' (legacy) and 'learningObjectives' (new)
      const allGoals = courseData?.learningObjectives || courseData?.goals || [];

      return topics.map((topic, idx) => {
        const rawActivities = topic.activities || topic.Activities || [];
        return {
          ...topic,
          id: topic.id || idx + 1,
          title: topic.title || topic.Title || topic.TopicName || 'Untitled Topic',
          activities: rawActivities.map((act, actIdx) => {
            const actorValue = act.actor || act.Actor || act.collaboration || act.role || '';
            const facilitatorValue =
              act.facilitatorRole || act.FacilitatorRole || act.facilitator || '';

            // IMPORTANT: Preserve objectiveIds (array) and objectiveId (singular) from original activity
            // Support both new (objectiveId/objectiveIds) and legacy (goalId/goalIds) field names
            // This ensures objectives don't disappear visually when activities are updated
            const objectiveIds = act.objectiveIds || act.goalIds || (act.objectiveId ? [act.objectiveId] : act.goalId ? [act.goalId] : []) || [];
            const objectiveId = act.objectiveId || act.goalId || act.GoalId || act.goal_id || (objectiveIds.length > 0 ? objectiveIds[0] : undefined);

            // Resolve objectiveId/objectiveIds to actual objective text for display
            let goalsList = normalizeGoalEntries(
              act.goals || act.Goals || act.goal || act.alignedGoals
            );

            // If activity has objectiveId/objectiveIds (UUIDs), resolve them to objective text
            if (allGoals.length > 0) {
              const resolvedGoals = [];
              if (objectiveIds.length > 0) {
                // Resolve all objectiveIds to their text
                objectiveIds.forEach(oid => {
                  const linkedObjective = allGoals.find(g => g.id === oid);
                  if (linkedObjective && linkedObjective.text) {
                    resolvedGoals.push(linkedObjective.text);
                  }
                });
              } else if (objectiveId) {
                // Fallback to single objectiveId
                const linkedObjective = allGoals.find(g => g.id === objectiveId);
                if (linkedObjective && linkedObjective.text) {
                  resolvedGoals.push(linkedObjective.text);
                }
              }

              // Use resolved objectives if we found any, otherwise keep existing goalsList
              if (resolvedGoals.length > 0) {
                goalsList = resolvedGoals;
              }
            }

            return {
              ...act,
              id: act.id || act.activityId || act.Id || actIdx + 1,
              title: act.title || act.Title || act.name || 'Untitled Activity',
              description: act.description || act.Description || act.details || '',
              actor: actorValue,
              facilitatorRole: facilitatorValue,
              bloomLevel:
                act.bloomLevel ||
                act.BloomLevel ||
                act.BloomType ||
                act.bloom ||
                'Understanding',
              resourceType:
                act.resourceType || act.ResourceType || act.resource || act.resourceKind || '',
              resourceTitle:
                act.resourceTitle ||
                act.ResourceTitle ||
                act.resourceName ||
                act.resource_title ||
                '',
              resourceDescription:
                act.resourceDescription ||
                act.ResourceDescription ||
                act.resourceNotes ||
                act.resourcesDescription ||
                '',
              goals: goalsList,
              objectiveIds: objectiveIds.length > 0 ? objectiveIds : undefined,  // Preserve objectiveIds array
              objectiveId: objectiveId,  // Keep objectiveId for reference
              duration:
                parseDuration(
                  act.duration ||
                  act.Duration ||
                  act.durationMinutes ||
                  act.estimatedMinutes ||
                  act.EstimatedMinutes ||
                  act.duration_min ||
                  act.duration_mins
                ) || undefined
            };
          })
        };
      });
    },
    [topics, courseData]
  );

  const handleEdit = async (path, value) => {
    if (!updateCourseData) return;
    const pathParts = path.split('.');
    const updatedTopics = JSON.parse(JSON.stringify(topics));

    let current = { topics: updatedTopics };
    for (let i = 0; i < pathParts.length - 1; i++) {
      const part = pathParts[i];
      const nextPart = pathParts[i + 1];
      const index = parseInt(nextPart);

      if (!isNaN(index)) {
        current = current[part][index];
        i++;
      } else {
        current = current[part];
      }
    }

    const lastKey = pathParts[pathParts.length - 1];
    current[lastKey] = value;

    // CRITICAL: Ensure objectiveId/objectiveIds are preserved on all activities
    // Support both new (objectiveId/objectiveIds) and legacy (goalId/goalIds) field names
    // This prevents objectives from disappearing when editing activity fields
    if (topics) {
      // Build a map of original activities by ID for efficient lookup
      const originalActivityMap = new Map();
      topics.forEach((topic) => {
        if (topic.activities) {
          topic.activities.forEach((activity) => {
            if (activity.id) {
              originalActivityMap.set(activity.id, activity);
            }
          });
        }
      });

      // Preserve objective links in updated activities
      updatedTopics.forEach((topic) => {
        if (topic.activities) {
          topic.activities.forEach((activity) => {
            const originalActivity = activity.id ? originalActivityMap.get(activity.id) : null;

            if (originalActivity) {
              // Get original objective links (support both new and legacy field names)
              const originalObjectiveIds = originalActivity.objectiveIds || originalActivity.goalIds;
              const originalObjectiveId = originalActivity.objectiveId || originalActivity.goalId;

              // Preserve objectiveIds if it exists in the original
              if (originalObjectiveIds && Array.isArray(originalObjectiveIds) && originalObjectiveIds.length > 0) {
                activity.objectiveIds = [...originalObjectiveIds];
              } else if (originalObjectiveId) {
                // Fallback to objectiveId if objectiveIds doesn't exist
                activity.objectiveIds = [originalObjectiveId];
              }

              // Preserve objectiveId
              if (originalObjectiveId) {
                activity.objectiveId = originalObjectiveId;
              } else if (activity.objectiveIds && activity.objectiveIds.length > 0) {
                // Set objectiveId from first objectiveIds entry if objectiveId is missing
                activity.objectiveId = activity.objectiveIds[0];
              }
            }
          });
        }
      });
    }

    await updateCourseData({ topics: updatedTopics });
    setTopics(updatedTopics);
  };

  return (
    <ActivitiesStepShell
      topics={normalizedTopics}
      goals={courseData?.learningObjectives || courseData?.goals || []}
      headerTitle="Σχεδιασμός & Δραστηριότητες"
      headerSubtitle="Συζητήστε με τον Coby για να βελτιώσετε τις δραστηριότητες και το περιεχόμενο του μαθήματος"
      onEdit={handleEdit}
    />
  );
}
